import { mdiMicrophone, mdiVolumeVibrate, mdiZodiacAquarius } from "@mdi/js";
import { StatusDataType } from "@soft-db/monitoring-api-client";
import { useDataFormatter } from "../../../composable/data-formatter";
import { LiveStatus } from "./live-status";

export type LiveDataBadge = {
	icon: string;
	value: string;
	name: string;
};

export function useLiveDataBadge(
	status: MaybeRefOrGetter<LiveStatus | null>
): ComputedRef<LiveDataBadge | null> {
	// Two fraction digits
	const vibrationFormatter = new Intl.NumberFormat(undefined, {
		style: "decimal",
		minimumIntegerDigits: 1,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

	// One fraction digit
	const soundFormatter = new Intl.NumberFormat(undefined, {
		style: "decimal",
		minimumIntegerDigits: 1,
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	});

	const { generateDataLabel } = useDataFormatter();

	return computed(() => {
		const statusVal = toValue(status);
		if (statusVal == null) {
			return null;
		}

		const { type, value } = statusVal;

		if (value == null) {
			return null;
		}

		let icon: string = "";
		let unit: string = "";
		let name: string = "";
		let formattedValue: string = "";

		switch (type) {
			case StatusDataType.SLM:
				unit = "dBA";
				icon = mdiMicrophone;
				name = "SPL";
				formattedValue = soundFormatter.format(value);
				break;

			case StatusDataType.ACC:
				unit = "m/s\u00B2";
				icon = mdiZodiacAquarius;
				name = generateDataLabel({
					type: "vibration",
					subType: "acceleration",
					unit: "mm/s²",
					data: "peak",
					axis: "v_sum",
				});
				formattedValue = vibrationFormatter.format(value);
				break;

			case StatusDataType.VEL:
				unit = "mm/s";
				icon = mdiZodiacAquarius;
				name = generateDataLabel({
					type: "vibration",
					subType: "velocity",
					unit: "mm/s",
					data: "peak",
					axis: "v_sum",
				});
				formattedValue = vibrationFormatter.format(value * 1000);
				break;

			case StatusDataType.OPL:
				unit = "dB";
				icon = mdiVolumeVibrate;
				name = generateDataLabel({
					type: "overpressure",
					unit: "dB",
					data: "peak",
				});
				formattedValue = soundFormatter.format(value);
				break;
		}

		return {
			icon,
			name,
			value: `${formattedValue} ${unit}`,
		};
	});
}
